// Copyright (c) 2026 Aravind Adari. All rights reserved.

import { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import StarOneLogo from "../components/StarOneLogo";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [pwdErr, setPwdErr] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validateEmail = (v: string) => {
    if (!v.trim()) return "Email address is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Please enter a valid email address.";
    return "";
  };

  const validatePwd = (v: string) => {
    if (!v) return "Password is required.";
    if (v.length < 6) return "Password must be at least 6 characters.";
    return "";
  };

  const handleLogin = async () => {
    const eErr = validateEmail(email);
    const pErr = validatePwd(pwd);
    setEmailErr(eErr);
    setPwdErr(pErr);
    if (eErr || pErr) return;

    setLoading(true);
    setErr("");
    try {
      await signInWithEmailAndPassword(auth, email, pwd);
      navigate("/dashboard");
    } catch (e: unknown) {
      if (e instanceof Error) {
        const code = (e as { code?: string }).code ?? "";
        switch (code) {
          case "auth/user-not-found":
            setErr("No account found with this email. Please check and try again.");
            break;
          case "auth/wrong-password":
          case "auth/invalid-credential":
            setErr("Incorrect password. Please try again or contact your admin.");
            break;
          case "auth/invalid-email":
            setErr("The email address format is invalid.");
            break;
          case "auth/user-disabled":
            setErr("This account has been disabled. Please contact your administrator.");
            break;
          case "auth/too-many-requests":
            setErr("Too many failed attempts. Please wait a few minutes and try again.");
            break;
          case "auth/network-request-failed":
            setErr("Network error. Please check your internet connection and try again.");
            break;
          default:
            setErr("Unable to sign in. Please check your credentials and try again.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0D47A1",
        position: "relative",
        overflow: "hidden",
        px: 2,
      }}
    >
      {/* Decorative circles */}
      <Box sx={{ position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "rgba(255,255,255,0.04)", top: -100, right: -100 }} />
      <Box sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.03)", bottom: -80, left: -80 }} />
      <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.05)", top: "40%", left: "10%" }} />

      <Paper
        elevation={16}
        sx={{
          maxWidth: 440,
          width: "100%",
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          position: "relative",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Logo Area */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <StarOneLogo size={64} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: "primary.main", letterSpacing: -0.5 }}>
            StarOne
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, fontWeight: 500, letterSpacing: 2, textTransform: "uppercase", fontSize: "0.7rem" }}>
            Timesheet Management System
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", px: 1 }}>Sign in to continue</Typography>
        </Divider>

        {err && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} variant="outlined">
            {err}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (emailErr) setEmailErr(""); }}
          onBlur={() => setEmailErr(validateEmail(email))}
          onKeyDown={handleKeyDown}
          margin="normal"
          autoFocus
          autoComplete="email"
          error={!!emailErr}
          helperText={emailErr}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon sx={{ color: emailErr ? "error.main" : "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
        <TextField
          fullWidth
          label="Password"
          type={showPwd ? "text" : "password"}
          value={pwd}
          onChange={(e) => { setPwd(e.target.value); if (pwdErr) setPwdErr(""); }}
          onBlur={() => setPwdErr(validatePwd(pwd))}
          onKeyDown={handleKeyDown}
          margin="normal"
          autoComplete="current-password"
          error={!!pwdErr}
          helperText={pwdErr}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: pwdErr ? "error.main" : "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPwd(!showPwd)}
                  edge="end"
                  size="small"
                  aria-label={showPwd ? "Hide password" : "Show password"}
                >
                  {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleLogin}
          disabled={loading}
          sx={{
            mt: 3,
            py: 1.5,
            fontSize: "1rem",
            fontWeight: 700,
            borderRadius: 2,
            backgroundColor: "#1565C0",
            boxShadow: "0 6px 20px rgba(21,101,192,0.35)",
            "&:hover": {
              backgroundColor: "#0D47A1",
              boxShadow: "0 8px 28px rgba(21,101,192,0.45)",
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
        </Button>

        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography variant="caption" sx={{ color: "text.disabled", display: "block" }}>
            StarOne Broadband Pvt Ltd
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            Employee Management &amp; Payroll System
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
